import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Button, List, Popconfirm, Spin, Tooltip, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { listConversations, deleteConversation } from './botApi';

/**
 * 会话历史侧栏。
 * Props:
 *  - currentId: 当前选中的 conversation id（null 表示"新对话"）
 *  - onSelect(id): 点击某条会话
 *  - onNew(): 点击"新对话"按钮
 * Ref:
 *  - reload(): 外部触发刷新（如新消息后）
 */
const BotSidebar = forwardRef(function BotSidebar({ currentId, onSelect, onNew }, ref) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await listConversations();
            setItems(rows);
        } catch (e) {
            message.error('加载会话列表失败');
        } finally {
            setLoading(false);
        }
    }, []);

    useImperativeHandle(ref, () => ({ reload }), [reload]);

    useEffect(() => { reload(); }, [reload]);

    const handleDelete = async (id, e) => {
        e.stopPropagation?.();
        try {
            await deleteConversation(id);
            await reload();
            if (id === currentId) onNew();
        } catch (err) {
            message.error('删除失败');
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ padding: 8 }}>
                <Button type="primary" block icon={<PlusOutlined />} onClick={onNew}>新对话</Button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px 8px' }}>
                {loading && items.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                ) : (
                    <List
                        size="small"
                        dataSource={items}
                        locale={{ emptyText: '暂无历史会话' }}
                        renderItem={(item) => {
                            const active = item.id === currentId;
                            return (
                                <List.Item
                                    onClick={() => onSelect(item.id)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '8px 10px',
                                        borderRadius: 4,
                                        background: active ? 'rgba(91,192,222,0.18)' : 'transparent',
                                        marginBottom: 2,
                                        border: 'none',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8 }}>
                                        <Tooltip title={item.title} mouseEnterDelay={0.4}>
                                            <span style={{
                                                flex: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                color: '#FFF',
                                            }}>
                                                {item.title || '(无标题)'}
                                            </span>
                                        </Tooltip>
                                        <Popconfirm
                                            title="删除此会话？"
                                            onConfirm={(e) => handleDelete(item.id, e)}
                                            onCancel={(e) => e?.stopPropagation?.()}
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<DeleteOutlined />}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ color: 'rgba(255,255,255,0.6)' }}
                                            />
                                        </Popconfirm>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                )}
            </div>
        </div>
    );
});

export default BotSidebar;
